/**
 * bimLuminaireParts — the parametric LUMINAIRE geometry, ported from the
 * house-ifc Python device module (card t_d267b30d).
 *
 * The owner's report: *"i have a lamp selested and it gives me shades model
 * here"* — a light device was offered the shade families, so the 3D-model
 * section had no luminaire to attach. This module is that family's geometry:
 * every part below is `devices/luminaire/luminaire.py`'s own solid list for the
 * seven variants, in the module's own numbers (mm → metres), so a fixture
 * attached in the app is the fixture the house's pipeline authors:
 *
 *   spot / pendant / wall / pole / strip / bulb / panel  →  *_solids(p)
 *
 * Local frame of every variant (the module's contract):
 *   origin = the fixture's ANCHOR (its mount) — spot: the ceiling cut-out
 *   centre; pendant/bulb: the ceiling attachment, hanging down to −drop; wall:
 *   the backplate's centre ON the wall plane (y = 0), the body projecting +Y;
 *   pole: the TOP of the head, the unit reaching down to −h (grade); strip /
 *   panel: the centre of the run, +X along it, the diffuser facing −Z.
 *   +Z is up, +Y is out of the mounting plane.
 *
 * The same three materials the module styles its parts with: the matte body
 * (the operator's RAL tone), the ACCENT (brass / alu / black rings, caps and
 * liners) and the OPAL diffuser — the one part a light comes out of, and the
 * one part both glow layers drive (`utils/bimGlow.ts`).
 *
 * Pure: no Vue, no three.js. The renderer (`composables/useBimDeviceModels.ts`)
 * turns these boxes, cylinders, frustums and spheres into meshes;
 * `bimLuminaireParts.test.ts` pins the numbers against the module's own
 * constants so a look-alike drift fails a test rather than the picture.
 */

import {
  LUMINAIRE_DEFAULT_SIZE_MM,
  type LuminaireAccent,
  type LuminaireVariant,
  type Vec3,
} from './bimLuminaireSpec';

export type { Vec3 };

/** Which shared material a part uses (the module's style keys). */
export type LuminairePartMaterial = 'body' | 'accent' | 'opal';

/**
 * How a part is drawn. The module authors solids, not boxes: a `spot` is two
 * cylinders, a reflector cone, a lens; a `bulb` is a globe. The renderer builds
 * the matching primitive (the same shape, fewer segments than the module's
 * faceted lathes — the app's meshes are a view of the fixture, not the IFC).
 */
export type LuminairePartShape = 'box' | 'cylinder' | 'sphere' | 'frustum';

export interface LuminairePartBox {
  /** Stable slot key: 'canopy' | 'cord' | 'shade' | 'ring' | 'lens' | … */
  key: string;
  material: LuminairePartMaterial;
  shape: LuminairePartShape;
  /** The part's centre, metres, in the fixture's local frame. */
  center: Vec3;
  /**
   * Full extents, metres. What each component means by shape:
   *   box       [dx, dy, dz]
   *   cylinder  [diameter, diameter, height] — the axis is local Z (up)
   *   frustum   [2·rBottom, 2·rBottom, height] (`radii` carries both ends)
   *   sphere    [diameter, diameter, diameter]
   */
  size: Vec3;
  /** Frustums only: [bottom radius, top radius] in metres. */
  radii?: [number, number];
}

const MM = 0.001;

// ─── the module's own part-list constants ───────────────────────────────────

/** `pole_solids()` — head height, base plate and the shaft's radius (mm). */
export const POLE = { headH: 190 * MM, baseH: 30 * MM, baseR: 75 * MM, shaftR: 28 * MM } as const;
/** `pendant_solids()` — the canopy's diameter/height and the cord's radius (mm). */
export const PENDANT = { canopyDia: 120 * MM, canopyH: 14 * MM, cordR: 4 * MM } as const;
/** `bulb_solids()` — cord length/radius and the socket's radius/height (mm). */
export const BULB = { cordH: 60 * MM, cordR: 5 * MM, socketR: 19 * MM, socketH: 58 * MM } as const;

// ─── the module's solid helpers (`_box`, `_cyl`, `_frustum`, sphere_solid) ───

/** `_box(x0, y0, z0, dx, dy, dz)`: an axis-aligned box; dz < 0 hangs downward. */
function box(key: string, material: LuminairePartMaterial, x0: number, y0: number, z0: number, dx: number, dy: number, dz: number): LuminairePartBox {
  const zLow = dz < 0 ? z0 + dz : z0;
  return {
    key,
    material,
    shape: 'box',
    center: [x0 + dx / 2, y0 + dy / 2, zLow + Math.abs(dz) / 2],
    size: [dx, dy, Math.abs(dz)],
  };
}

/** `_cyl(x, y, z0, r, dz)`: a vertical n-gon cylinder about (x, y); dz < 0 hangs. */
function cyl(key: string, material: LuminairePartMaterial, x: number, y: number, z0: number, r: number, dz: number): LuminairePartBox {
  const zLow = dz < 0 ? z0 + dz : z0;
  return {
    key,
    material,
    shape: 'cylinder',
    center: [x, y, zLow + Math.abs(dz) / 2],
    size: [2 * r, 2 * r, Math.abs(dz)],
  };
}

/** `_frustum(r_bot, z_bot, r_top, z_top)`: a cone / frustum about the vertical axis. */
function frustum(key: string, material: LuminairePartMaterial, rBot: number, zBot: number, rTop: number, zTop: number): LuminairePartBox {
  const zLow = Math.min(zBot, zTop);
  const height = Math.abs(zTop - zBot);
  const bottom = zTop < zBot ? rTop : rBot;
  const top = zTop < zBot ? rBot : rTop;
  return {
    key,
    material,
    shape: 'frustum',
    center: [0, 0, zLow + height / 2],
    size: [2 * bottom, 2 * bottom, height],
    radii: [bottom, top],
  };
}

/** `sphere_solid(r, zc)`: a globe centred on the vertical axis at height zc. */
function sphere(key: string, material: LuminairePartMaterial, r: number, zc: number): LuminairePartBox {
  return {
    key,
    material,
    shape: 'sphere',
    center: [0, 0, zc],
    size: [2 * r, 2 * r, 2 * r],
  };
}

// ─── the seven variants (the module's *_solids, verbatim) ───────────────────

/** `spot_solids()` — downlight centred on the origin: cap + can, cone, lens. */
function spotParts(size: SizeOverrides): LuminairePartBox[] {
  const dia = size.dia;
  const h = size.height;
  const r = dia / 2;
  const zTop = 0.45 * h;
  const zCapB = 0.32 * h;
  const zCanB = -0.06 * h;
  return [
    cyl('cap', 'body', 0, 0, zCapB, r, zTop - zCapB),
    cyl('can', 'body', 0, 0, zCanB, r - 4 * MM, zCapB - zCanB),
    frustum('cone', 'accent', r - 15 * MM, -0.51 * h, r - 4 * MM, zCanB),
    cyl('lens', 'opal', 0, 0, -0.55 * h, r - 16 * MM, 6 * MM),
  ];
}

/** `pendant_solids()` — canopy, cord, flared shade, brass ring, opal lens. */
function pendantParts(size: SizeOverrides): LuminairePartBox[] {
  const dia = size.shade_dia;
  const sh = size.shade_h;
  const drop = size.drop;
  const zShadeT = -(drop - sh);
  return [
    cyl('canopy', 'body', 0, 0, -PENDANT.canopyH, PENDANT.canopyDia / 2, PENDANT.canopyH),
    cyl('cord', 'body', 0, 0, zShadeT, PENDANT.cordR, -PENDANT.canopyH - zShadeT),
    frustum('shade', 'body', dia / 2, -drop, dia / 2 - 12 * MM, zShadeT),
    cyl('ring', 'accent', 0, 0, -drop - 12 * MM, dia / 2 + 4 * MM, 12 * MM),
    cyl('lens', 'opal', 0, 0, -drop - 8 * MM, dia / 2 - 40 * MM, 8 * MM),
  ];
}

/** `wall_solids()` — backplate + body (black), brass liner, opal slot. */
function wallParts(size: SizeOverrides): LuminairePartBox[] {
  const w = size.w;
  const h = size.h;
  const d = size.depth;
  return [
    box('backplate', 'body', -w / 2 - 10 * MM, 0, -h / 2 - 10 * MM, w + 20 * MM, 8 * MM, h + 20 * MM),
    box('body', 'body', -w / 2, 8 * MM, -h / 2, w, d - 8 * MM, h),
    box('liner', 'accent', -w * 0.31, d - 16 * MM, -h * 0.44, w * 0.62, 26 * MM, h * 0.88),
    box('slot', 'opal', -w * 0.18, d + 2 * MM, -h * 0.38, w * 0.36, 14 * MM, h * 0.76),
  ];
}

/** `pole_solids()` — head with brass + opal bands, shaft, base plate. */
function poleParts(size: SizeOverrides): LuminairePartBox[] {
  const h = size.h;
  const hd = size.head_dia;
  return [
    cyl('head', 'body', 0, 0, -POLE.headH, hd / 2, POLE.headH),
    cyl('band', 'accent', 0, 0, -88 * MM, hd / 2 + 2 * MM, 30 * MM),
    cyl('opal-band', 'opal', 0, 0, -58 * MM, hd / 2 + 1 * MM, 30 * MM),
    cyl('shaft', 'body', 0, 0, -h + POLE.baseH, POLE.shaftR, h - POLE.baseH - POLE.headH),
    cyl('base', 'body', 0, 0, -h, POLE.baseR, POLE.baseH),
  ];
}

/** `strip_solids()` — linear LED profile: profile, opal face, brass end caps. */
function stripParts(size: SizeOverrides): LuminairePartBox[] {
  const L = size.length;
  return [
    box('profile', 'body', -L / 2, -10 * MM, -8 * MM, L, 20 * MM, 16 * MM),
    box('face', 'opal', -L / 2 + 35 * MM, -7 * MM, -11 * MM, L - 70 * MM, 14 * MM, 4 * MM),
    box('cap-l', 'accent', -L / 2, -11.5 * MM, -9.5 * MM, 27 * MM, 23 * MM, 19 * MM),
    box('cap-r', 'accent', L / 2 - 27 * MM, -11.5 * MM, -9.5 * MM, 27 * MM, 23 * MM, 19 * MM),
  ];
}

/** `bulb_solids()` — suspended globe: cord, brass socket, opal globe. */
function bulbParts(size: SizeOverrides): LuminairePartBox[] {
  const dia = size.bulb_dia;
  const drop = size.drop;
  const r = dia / 2;
  return [
    cyl('cord', 'body', 0, 0, -BULB.cordH, BULB.cordR, BULB.cordH),
    cyl('socket', 'accent', 0, 0, -118 * MM, BULB.socketR, BULB.socketH),
    sphere('globe', 'opal', r, -drop + r),
  ];
}

/** `panel_solids()` — slim under-cabinet bar: housing, opal face, brass caps. */
function panelParts(size: SizeOverrides): LuminairePartBox[] {
  const L = size.length;
  const W = size.width;
  return [
    box('housing', 'body', -L / 2, -W / 2, -8 * MM, L, W, 16 * MM),
    box('face', 'opal', -L / 2 + 25 * MM, -W / 2 + 5 * MM, -11 * MM, L - 50 * MM, W - 10 * MM, 4 * MM),
    box('cap-l', 'accent', -L / 2, -W / 2 - 1 * MM, -9 * MM, 25 * MM, W + 2 * MM, 18 * MM),
    box('cap-r', 'accent', L / 2 - 25 * MM, -W / 2 - 1 * MM, -9 * MM, 25 * MM, W + 2 * MM, 18 * MM),
  ];
}

const BUILDERS: Record<LuminaireVariant, (size: SizeOverrides) => LuminairePartBox[]> = {
  spot: spotParts,
  pendant: pendantParts,
  wall: wallParts,
  pole: poleParts,
  strip: stripParts,
  bulb: bulbParts,
  panel: panelParts,
};

/**
 * The variant names the operator reads — the module's own vocabulary (the words
 * the pipeline classifies device names into), with the shape each one is, so
 * the choice is a fixture and not a code.
 */
export const LUMINAIRE_VARIANT_TITLES: Record<LuminaireVariant, { en: string; sl: string }> = {
  spot: { en: 'Spot / downlight', sl: 'Vgradna luč' },
  pendant: { en: 'Pendant', sl: 'Viseča svetilka' },
  wall: { en: 'Wall sconce', sl: 'Stenska svetilka' },
  pole: { en: 'Pole / bollard', sl: 'Stebriček' },
  strip: { en: 'LED strip', sl: 'LED profil' },
  bulb: { en: 'Globe bulb', sl: 'Žarnica' },
  panel: { en: 'Under-cabinet panel', sl: 'Podomarski panel' },
};

/** The accent tones, named as the module's own table does (brass / alu / black). */
export const LUMINAIRE_ACCENT_TITLES: Record<LuminaireAccent, { en: string; sl: string }> = {
  brass: { en: 'Brass', sl: 'Med' },
  alu: { en: 'Aluminium', sl: 'Aluminij' },
  black: { en: 'Black', sl: 'Črna' },
};

/** The size keys a variant reads — the module's DEFAULTS keys, that and no more. */
export type SizeOverrides = Record<string, number>;

/** The variant's effective size: the module's DEFAULTS, overridden per key (mm). */
export function luminaireSizeMm(variant: LuminaireVariant, overrides: SizeOverrides = {}): SizeOverrides {
  const out: SizeOverrides = { ...LUMINAIRE_DEFAULT_SIZE_MM[variant] };
  for (const key of Object.keys(out)) {
    const value = overrides[key];
    if (typeof value === 'number' && Number.isFinite(value) && value > 0) out[key] = value;
  }
  return out;
}

/**
 * Every part of one luminaire variant, in the module's own build order.
 *
 * The size table is authored in MILLIMETRES (the module's `DEFAULTS`, and what
 * the editor's fields write); the parts come back in METRES, the scene's unit —
 * the same mm → m step `_box(..., s=...)` does when the module scales its own
 * numbers, applied once here so the builders below only ever speak one unit.
 */
export function luminaireParts(variant: LuminaireVariant, overrides: SizeOverrides = {}): LuminairePartBox[] {
  const build = BUILDERS[variant] ?? BUILDERS.spot;
  const metres: SizeOverrides = {};
  for (const [key, value] of Object.entries(luminaireSizeMm(variant, overrides))) metres[key] = value * MM;
  return build(metres);
}

/** The parts a light comes out of — the opal diffuser (every variant has one). */
export function luminaireEmitterParts(parts: readonly LuminairePartBox[]): LuminairePartBox[] {
  return parts.filter((part) => part.material === 'opal');
}

/**
 * The emitter's extent in the fixture's local frame — the halo's sizing source
 * (the live layer measures the served twin's diffuser the same way, from its
 * own meshes).
 */
export function luminaireEmitterBounds(parts: readonly LuminairePartBox[]): { center: Vec3; size: Vec3 } | null {
  const emitters = luminaireEmitterParts(parts);
  if (emitters.length === 0) return null;
  let minX = Infinity;
  let minY = Infinity;
  let minZ = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  let maxZ = -Infinity;
  for (const part of emitters) {
    const half: Vec3 = [part.size[0] / 2, part.size[1] / 2, part.size[2] / 2];
    minX = Math.min(minX, part.center[0] - half[0]);
    maxX = Math.max(maxX, part.center[0] + half[0]);
    minY = Math.min(minY, part.center[1] - half[1]);
    maxY = Math.max(maxY, part.center[1] + half[1]);
    minZ = Math.min(minZ, part.center[2] - half[2]);
    maxZ = Math.max(maxZ, part.center[2] + half[2]);
  }
  return {
    center: [(minX + maxX) / 2, (minY + maxY) / 2, (minZ + maxZ) / 2],
    size: [maxX - minX, maxY - minY, maxZ - minZ],
  };
}

/**
 * The extent of EVERY part — the fixture's own bounding box, as the variants'
 * proportions predict it (a pole spans its full height, a pendant hangs to
 * −drop). Used by the tests and by the editor's own sanity line.
 */
export function luminaireBounds(parts: readonly LuminairePartBox[]): { min: Vec3; max: Vec3 } | null {
  if (parts.length === 0) return null;
  const min: Vec3 = [Infinity, Infinity, Infinity];
  const max: Vec3 = [-Infinity, -Infinity, -Infinity];
  for (const part of parts) {
    for (let axis = 0; axis < 3; axis += 1) {
      min[axis] = Math.min(min[axis], part.center[axis] - part.size[axis] / 2);
      max[axis] = Math.max(max[axis], part.center[axis] + part.size[axis] / 2);
    }
  }
  return { min, max };
}
